import type { QuerySpec } from '@helios/powersync-client-core';
import { resolveServiceLaboratoryDepartmentId } from './laboratory-departments-logic.js';

// ─── Types ───

export interface AppointmentServiceRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  name: string | null;
  category_id: string | null;
  description: string | null;
  typical_duration_mins: number | null;
  price_cents: number | null;
  active: number | null; // 0/1
  // PRD.305 — canonical laboratory-test marker (0/1). Optional so rows synced
  // before this column existed read back cleanly; absent/null means false.
  is_lab_test?: number | null;
  // PRD.329 — Laboratory Department (only meaningful for lab tests) and the
  // read-only provisioning identity.
  laboratory_department_id?: string | null;
  external_code?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Row shape when joining category name */
export interface AppointmentServiceWithCategoryRow extends AppointmentServiceRow {
  category_name: string | null;
  category_color_hex: string | null;
  // PRD.329 — joined department (null when unassigned / not synced yet).
  laboratory_department_name?: string | null;
  laboratory_department_active?: number | null;
}

// ─── Queries ───

/**
 * Fetch all appointment services, ordered by name.
 */
export function qAppointmentServices(): QuerySpec<[]> {
  return {
    key: ['appointment-services'] as const,
    sql: `SELECT * FROM appointment_services ORDER BY name ASC`,
    params: [],
    watch: ['appointment_services'],
  };
}

/**
 * Fetch services with category name + color joined.
 */
export function qAppointmentServicesWithCategory(): QuerySpec<[]> {
  return {
    key: ['appointment-services-with-category'] as const,
    sql: `SELECT s.*, c.name AS category_name, c.color_hex AS category_color_hex,
        d.name AS laboratory_department_name, d.active AS laboratory_department_active
      FROM appointment_services s
      LEFT JOIN appointment_categories c ON s.category_id = c.id
      LEFT JOIN laboratory_departments d ON d.id = s.laboratory_department_id
      ORDER BY s.name ASC`,
    params: [],
    watch: ['appointment_services', 'appointment_categories', 'laboratory_departments'],
  };
}

/**
 * Fetch active services only (for autocomplete / service picker).
 */
export function qActiveAppointmentServices(): QuerySpec<[]> {
  return {
    key: ['appointment-services-active'] as const,
    // is_lab_test lets booking pickers honour Laboratory Settings' "hide lab
    // tests from booking" (PRD.329 follow-up).
    sql: `SELECT id, name, price_cents, is_lab_test FROM appointment_services WHERE active = 1 ORDER BY name ASC`,
    params: [],
    watch: ['appointment_services'],
  };
}

/**
 * PRD.267 — candidate row for the package builder's appointment-service picker.
 */
export interface PackageServiceCandidateRow {
  id: string;
  name: string | null;
  price_cents: number | null;
}

/**
 * PRD.267 — search active appointment services eligible to be bundled into a
 * package. Empty `search` returns the (capped) list. Mirrors the inventory
 * `qPackageChildCandidates` shape so the builder can merge both result sets.
 */
export function qPackageServiceCandidates(
  orgId: string,
  search: string,
): QuerySpec<[string, string, string]> {
  const term = `%${search.trim().toLowerCase()}%`;
  return {
    key: ['package-service-candidates', orgId, search] as const,
    sql: `SELECT id, name, price_cents
          FROM appointment_services
          WHERE org_id = ?
            AND COALESCE(active, 1) = 1
            AND (? = '%%' OR LOWER(COALESCE(name, '')) LIKE ?)
          ORDER BY name ASC
          LIMIT 50`,
    params: [orgId, term, term],
    watch: ['appointment_services'],
  };
}

/**
 * PRD.209 — single appointment service row, used by the Invoice tab's
 * "Add Service from Appointment" button to resolve the appointment's
 * `service_id` to a name + price for the invoice line. Returns 0 or 1
 * row. Includes inactive services so historical appointments still
 * resolve; the invoice UI is responsible for any active-only gating.
 */
export function qAppointmentServiceById(serviceId: string): QuerySpec<[string]> {
  return {
    key: ['appointment-services', 'by-id', serviceId] as const,
    sql: `SELECT id, name, price_cents, active, category_id
          FROM appointment_services
          WHERE id = ?
          LIMIT 1`,
    params: [serviceId],
    watch: ['appointment_services'],
  };
}

// ─── Helpers ───

export function mapServiceRowToUI(row: AppointmentServiceWithCategoryRow) {
  return {
    id: row.id,
    name: row.name ?? '',
    categoryId: row.category_id ?? null,
    categoryName: row.category_name ?? '',
    categoryColorHex: row.category_color_hex ?? '#3b82f6',
    description: row.description ?? '',
    duration: row.typical_duration_mins ?? 0,
    priceCents: row.price_cents,
    active: row.active === 1,
    // PRD.305 — legacy rows have no value; they are not laboratory tests.
    isLabTest: row.is_lab_test === 1,
    // PRD.329 — Laboratory Department (null = Other / Unassigned).
    laboratoryDepartmentId: row.laboratory_department_id ?? null,
    laboratoryDepartmentName: row.laboratory_department_name ?? null,
    laboratoryDepartmentActive:
      row.laboratory_department_active == null ? null : row.laboratory_department_active === 1,
    externalCode: row.external_code ?? null,
  };
}

export function mapUIToServiceColumns(ui: {
  name: string;
  categoryId: string | null;
  description: string;
  duration: number;
  priceCents: number | null;
  active: boolean;
  /** PRD.305 — omitted by callers that predate laboratory support. */
  isLabTest?: boolean;
  /** PRD.329 — cleared automatically when the service is not a lab test. */
  laboratoryDepartmentId?: string | null;
}) {
  return {
    name: ui.name,
    category_id: ui.categoryId,
    description: ui.description || null,
    typical_duration_mins: ui.duration,
    price_cents: ui.priceCents,
    active: ui.active ? 1 : 0,
    is_lab_test: ui.isLabTest ? 1 : 0,
    laboratory_department_id: resolveServiceLaboratoryDepartmentId({
      isLabTest: !!ui.isLabTest,
      laboratoryDepartmentId: ui.laboratoryDepartmentId,
    }),
    updated_at: new Date().toISOString(),
  };
}
